import Avatar from './Avatar';
import { distinctOwners } from '../utils/wishOwner';

/**
 * A chip row for filtering a list of wishes down to ONE recipient.
 *
 * "Person" is really "recipient": pets are wish owners too, and they sit
 * alongside people here. Both come from the shared `wishOwner()` helper, so a
 * chip looks the same as the owner label on the card it filters to. Without pets
 * in the row, a pet's wishes would silently vanish whenever any filter was on.
 *
 * Options are derived from the wishes actually loaded rather than from a
 * membership list, so a chip never points at an empty result. Like the group
 * filter on the feed, the row hides itself when there's nothing to choose
 * between (fewer than 2 recipients).
 *
 * Controlled: the parent owns `value` (an owner key from `ownerKey()`, or null
 * for "everyone") so the filter can be composed with others and set from
 * elsewhere — the group Wishes tab sets it when you arrive from a birthday.
 */
export default function PersonFilterBar({ wishes, value, onChange, currentUserId = null, label = 'Everyone' }) {
    const owners = distinctOwners(wishes, currentUserId);

    if (owners.length < 2) return null;

    return (
        <div className="flex flex-wrap gap-2 mb-4">
            <button
                onClick={() => onChange(null)}
                aria-pressed={value === null}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                    value === null
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
            >
                {label}
            </button>

            {owners.map(({ key, owner, userId }) => {
                const active = value === key;
                const isSelf = !owner.isPet && userId === currentUserId;
                return (
                    <button
                        key={key}
                        onClick={() => onChange(active ? null : key)}
                        aria-pressed={active}
                        className={`pl-1 pr-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5 ${
                            active
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        <Avatar
                            name={owner.name}
                            url={owner.avatarUrl}
                            color={owner.avatarColor}
                            seed={owner.seed}
                            size={22}
                        />
                        {isSelf ? 'You' : owner.name}
                        {owner.isPet && <span aria-hidden="true">🐾</span>}
                    </button>
                );
            })}
        </div>
    );
}
